import { ref, reactive, computed } from 'vue'
import { apiService } from '../services/api.js'
import { useTelegramData } from './useTelegram.js'

// ---------------------------------------------------------------------------
// ЕДИНЫЙ ИСТОЧНИК ПРАВДЫ ДЛЯ PnL ПО ВСЕМУ ФРОНТЕНДУ
// ---------------------------------------------------------------------------
// Логика расчёта PnL для одной позиции (одинаково на всех экранах):
//   1. Если для позиции включён кастомный PnL в админке (custom_pnl) —
//      он ПЕРЕБИВАЕТ всё остальное.
//   2. Иначе — живой PnL по ценам Binance (как на вкладке Trade). Доступно
//      только для self-позиций (у копитрейд-позиций монета скрыта).
//   3. Иначе (нет живой цены — например копитрейд) — серверный PnL из /trade/state.
//
// Все экраны (Trade, Dashboard, Balance Details) используют этот composable,
// поэтому значение PnL везде идентично.
// ---------------------------------------------------------------------------

// Глобальное (синглтон) состояние — общее для всех компонентов.
const state = reactive({
  balance: 0,
  available: 0,
  margin_used: 0,
  equity: 0,
  unrealized_pnl: 0,
  positions: [],       // self-позиции
  orders: [],          // ожидающие лимитные ордера
  copy_positions: [],  // копитрейд-позиции (read-only)
  trading_blocked: false,
  loaded: false
})

// Карта кастомных PnL: { [position_id]: custom_pnl_percent }
const customPnls = ref({})

// Живые цены Binance по символу монеты ('BTC' -> price)
const livePrices = reactive({})

let stateTimer = null
let priceTimer = null
let refCount = 0
let visibilityHandler = null
let userDataRef = null

function currentUserId() {
  return userDataRef?.value?.id || null
}

const binanceSymbol = (coin) => (coin ? `${String(coin).toUpperCase().trim()}USDT` : null)

function livePriceFor(coin) {
  const c = coin ? String(coin).toUpperCase().trim() : null
  return c && livePrices[c] != null ? livePrices[c] : null
}

// Центральный расчёт PnL — ЕДИНЫЙ серверный источник: показываем то, что посчитал сервер
// в /trade/state (кастом+дрожание ±0.2% / цена CoinGecko / синтетика, клэмп −100%). Клиент,
// админка мини-аппа и веб-СРМ читают одно и то же значение → ничего не расходится.
function pnlFor(pos) {
  return {
    usdt: Number(pos.unrealized_pnl) || 0,
    percent: Number(pos.unrealized_pnl_percent) || 0,
    source: 'server'
  }
}

// self-позиции с уже посчитанным PnL и живой ценой
const displayPositions = computed(() => state.positions.map((p) => {
  const pnl = pnlFor(p)
  const live = livePriceFor(p.coin)
  return {
    ...p,
    current_price: live != null ? live : p.current_price,
    unrealized_pnl: pnl.usdt,
    unrealized_pnl_percent: pnl.percent
  }
}))

// копитрейд-позиции с PnL
const displayCopyPositions = computed(() => state.copy_positions.map((p) => {
  const pnl = pnlFor(p)
  return { ...p, unrealized_pnl: pnl.usdt, unrealized_pnl_percent: pnl.percent }
}))

// ордера с живой ценой (для отображения mark price)
const displayOrders = computed(() => state.orders.map((o) => {
  const live = livePriceFor(o.coin)
  return live == null ? o : { ...o, current_price: live }
}))

// Суммарный нереализованный PnL (self + copy) — одно значение для всего фронтенда
const totalUnrealizedPnl = computed(() =>
  displayPositions.value.reduce((s, p) => s + (Number(p.unrealized_pnl) || 0), 0) +
  displayCopyPositions.value.reduce((s, p) => s + (Number(p.unrealized_pnl) || 0), 0)
)

// Equity = wallet balance + суммарный нереализованный PnL
const totalEquity = computed(() => (Number(state.balance) || 0) + totalUnrealizedPnl.value)

// Флаг блокировки торговли (из /trade/state)
const tradingBlocked = computed(() => state.trading_blocked)

// PnL конкретной позиции по её id (для Dashboard). Возвращает {usdt, percent} или null.
function pnlById(positionId) {
  if (positionId == null) return null
  const self = state.positions.find((p) => (p.position_id || p.id) === positionId)
  if (self) return pnlFor(self)
  const copy = state.copy_positions.find((p) => (p.position_id || p.id) === positionId)
  if (copy) return pnlFor(copy)
  return null
}

async function loadState() {
  const userId = currentUserId()
  if (!userId) return
  try {
    const res = await apiService.tradeGetState(userId)
    if (res.ok && res.data) {
      state.balance = Number(res.data.balance) || 0
      state.available = Number(res.data.available) || 0
      state.margin_used = Number(res.data.margin_used) || 0
      state.equity = Number(res.data.equity) || 0
      state.unrealized_pnl = Number(res.data.unrealized_pnl) || 0
      state.positions = res.data.positions || []
      state.orders = res.data.orders || []
      state.copy_positions = res.data.copy_positions || []
      state.trading_blocked = !!res.data.trading_blocked
      state.loaded = true
    }
  } catch (e) {
    console.warn('[useTradePnl] loadState failed', e)
  }
}

async function loadCustomPnls() {
  const userId = currentUserId()
  if (!userId) return
  try {
    const res = await apiService.getUserCustomPnls(userId)
    if (res.ok && res.data) {
      let arr = []
      if (Array.isArray(res.data)) arr = res.data
      else if (Array.isArray(res.data.data)) arr = res.data.data
      const map = {}
      arr.forEach((item) => {
        if (item.is_active && item.position_id != null && item.custom_pnl_percent != null) {
          map[item.position_id] = Number(item.custom_pnl_percent)
        }
      })
      customPnls.value = map
    }
  } catch (e) {
    console.warn('[useTradePnl] loadCustomPnls failed', e)
  }
}

async function fetchLivePrices() {
  const coins = new Set()
  state.positions.forEach((p) => { if (p.coin) coins.add(p.coin) })
  state.orders.forEach((o) => { if (o.coin) coins.add(o.coin) })
  const symbols = [...coins].map(binanceSymbol).filter(Boolean)
  if (symbols.length === 0) return
  try {
    // через наш бэк-прокси (обход гео-блока Binance у клиента) — как и свечи графика
    const url = `/api/binance/price?symbols=${encodeURIComponent(JSON.stringify(symbols))}`
    const res = await fetch(url)
    if (!res.ok) {
      // цена не подтянулась → сообщаем в группу админов (сервер троттлит флуд)
      apiService.reportClientError('fetchLivePrices:http_' + res.status, 'price proxy not ok', { user_id: currentUserId() })
      return
    }
    const arr = await res.json()
    if (!Array.isArray(arr)) return
    arr.forEach((t) => {
      const price = parseFloat(t.price)
      if (t.symbol && isFinite(price)) {
        const coin = t.symbol.replace(/USDT$/i, '')
        livePrices[coin] = price
      }
    })
  } catch (e) {
    // цена с биржи не пришла (сеть/гео/таймаут) → останется серверная цена, но сообщим админам
    apiService.reportClientError('fetchLivePrices:exception', e, { user_id: currentUserId() })
  }
}

// Полная перезагрузка (после открытия/закрытия/отмены сделки)
async function refresh() {
  await Promise.all([loadState(), loadCustomPnls()])
  await fetchLivePrices()
}

export function useTradePnl() {
  const { userData } = useTelegramData()

  const start = async () => {
    userDataRef = userData
    refCount += 1
    if (refCount === 1) {
      await refresh()
      stateTimer = setInterval(() => { loadState(); loadCustomPnls() }, 3000)
      // раз в 1.5с тянем живые цены (для mark-price ордеров/калькулятора; PnL позиций — с сервера)
      priceTimer = setInterval(() => { fetchLivePrices() }, 1500)
      if (typeof document !== 'undefined' && !visibilityHandler) {
        visibilityHandler = () => { if (!document.hidden) refresh() }
        document.addEventListener('visibilitychange', visibilityHandler)
      }
    } else if (!state.loaded) {
      await refresh()
    }
  }

  const stop = () => {
    refCount = Math.max(0, refCount - 1)
    if (refCount === 0) {
      if (stateTimer) { clearInterval(stateTimer); stateTimer = null }
      if (priceTimer) { clearInterval(priceTimer); priceTimer = null }
      if (typeof document !== 'undefined' && visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler)
        visibilityHandler = null
      }
    }
  }

  return {
    state,
    livePrices,
    customPnls,
    displayPositions,
    displayCopyPositions,
    displayOrders,
    totalUnrealizedPnl,
    totalEquity,
    tradingBlocked,
    pnlFor,
    pnlById,
    livePriceFor,
    refresh,
    loadState,
    start,
    stop
  }
}
